import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2 } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { ME_KEY } from "@/lib/auth";
import { cn } from "@/lib/utils";

export function initialsOf(name: string | undefined | null) {
  return (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toLocaleUpperCase("tr"))
    .join("");
}

/** Profil fotoğrafı; yoksa baş harfler. */
export function Avatar({ name, url, className, tone = "accent" }: { name?: string | null; url?: string | null; className?: string; tone?: "accent" | "primary" }) {
  const [broken, setBroken] = useState(false);
  if (url && !broken) return <img src={url} alt="" onError={() => setBroken(true)} className={cn("shrink-0 rounded-full object-cover", className)} />;
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-bold",
        tone === "accent" ? "bg-gradient-to-br from-accent to-accent/70 text-accent-foreground" : "bg-primary text-primary-foreground ring-2 ring-accent/30",
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/** Seçilen görseli ortadan kare kırpıp 256 px JPEG'e çevirir. */
async function squareJpeg(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, 256, 256);
  ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, 256, 256);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.86));
  if (!blob) throw new Error("Görsel işlenemedi");
  return blob;
}

/**
 * Tıklanınca fotoğraf seçtiren avatar. userId "me" kendi fotoğrafı; başkası için
 * kullanıcı yönetimi yetkisi gerekir (sunucu doğrular).
 */
export function AvatarUpload({ userId, name, url, className, onChanged, onError }: { userId: string; name: string; url: string | null; className?: string; onChanged?: () => void; onError?: (m: string) => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function pick(file: File) {
    setBusy(true);
    try {
      await api.upload(`/uploads/avatar/${userId}`, await squareJpeg(file));
      await qc.invalidateQueries({ queryKey: ME_KEY });
      await qc.invalidateQueries({ queryKey: ["users"] });
      onChanged?.();
    } catch (err) {
      onError?.(err instanceof ApiError ? err.message : "Fotoğraf yüklenemedi");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" onClick={() => input.current?.click()} disabled={busy} className={cn("group relative shrink-0 rounded-full focus-visible:ring-4 focus-visible:ring-accent/40 focus-visible:outline-none", className)} title="Fotoğrafı değiştir" aria-label={`${name} fotoğrafını değiştir`}>
      <Avatar name={name} url={url} tone="primary" className="size-full text-xs" />
      <span className={cn("absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white transition-opacity", busy ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100")}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
      </span>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
          e.target.value = "";
        }}
      />
    </button>
  );
}
